import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EMAIL_SECURITY_LABELS, type EmailSecurity, type EmailSettings, type EmailTestResult } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';

/** Anbieter, bei denen nicht das normale Passwort des Postfachs funktioniert: Name des Feldes, Erklärung und Seite, auf der man es erzeugt */
interface PasswordHelp {
  label: string;
  warning: string;
  steps: string;
  url: string;
  linkText: string;
}

/** Voreinstellungen gängiger Anbieter. Die Angaben stehen in deren Hilfe, im Zweifel gilt dort die aktuelle Fassung. */
const PRESETS: { label: string; host: string; port: number; security: EmailSecurity; hint?: string; password?: PasswordHelp }[] = [
  { label: 'IONOS', host: 'smtp.ionos.de', port: 587, security: 'starttls', hint: 'Benutzername ist die vollständige Adresse, das Passwort das des Postfachs. Für Konten außerhalb Deutschlands gilt smtp.ionos.com.' },
  { label: 'Microsoft 365 / Outlook', host: 'smtp.office365.com', port: 587, security: 'starttls', hint: 'Der Versand über SMTP muss für das Postfach im Microsoft-365-Verwaltungsbereich erlaubt sein.' },
  { label: 'Google Workspace / Gmail', host: 'smtp.gmail.com', port: 465, security: 'ssl', hint: 'Mit Ihrem normalen Google-Passwort funktioniert der Versand nicht.',
    password: {
      label: 'App-Passwort',
      warning: 'Hier gehört ein App-Passwort hinein, nicht das Passwort, mit dem Sie sich bei Google anmelden.',
      steps: 'Ein App-Passwort ist ein eigenes, 16-stelliges Passwort nur für diese Verbindung. Sie erzeugen es in Ihrem Google-Konto unter „Sicherheit“ → „Bestätigung in zwei Schritten“ → „App-Passwörter“. Die Bestätigung in zwei Schritten muss dafür eingeschaltet sein. Bei Google Workspace kann Ihr Administrator App-Passwörter sperren.',
      url: 'https://myaccount.google.com/apppasswords',
      linkText: 'App-Passwort bei Google erstellen',
    },
  },
  { label: 'GMX', host: 'mail.gmx.net', port: 587, security: 'starttls', hint: 'In den GMX-Einstellungen muss „POP3 und IMAP Zugriff“ aktiviert sein.' },
  { label: 'WEB.DE', host: 'smtp.web.de', port: 587, security: 'starttls', hint: 'In den WEB.DE-Einstellungen muss „POP3 und IMAP Zugriff“ aktiviert sein.' },
  { label: 'STRATO', host: 'smtp.strato.de', port: 465, security: 'ssl' },
  { label: 'Telekom (t-online.de)', host: 'securesmtp.t-online.de', port: 465, security: 'ssl', hint: 'Mit Ihrem normalen Telekom-Passwort funktioniert der Versand nicht.',
    password: {
      label: 'E-Mail-Passwort',
      warning: 'Hier gehört das eigene E-Mail-Passwort hinein, nicht das Passwort Ihres Telekom-Logins.',
      steps: 'Sie legen es im Kundencenter unter „E-Mail“ → „E-Mail-Adresse verwalten“ → „Passwort für E-Mail-Programme“ an. Die genauen Menünamen ändert die Telekom gelegentlich.',
      url: 'https://email.t-online.de/',
      linkText: 'E-Mail-Einstellungen der Telekom öffnen',
    },
  },
];

const errorText = (e: unknown, fallback: string): string => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Einrichtung des E-Mail-Postfachs (KON-187). Wirkia prüft die Angaben gegen den Mailserver, bevor es sie speichert. Das Passwort wird nie angezeigt: Bei einer vorhandenen
 * Einrichtung bleibt das Feld leer, und das gespeicherte Passwort gilt weiter.
 */
export function EmailIntegration() {
  const client = useQueryClient();
  const current = useQuery({ queryKey: ['integrations', 'email'], queryFn: async () => (await api<{ data: EmailSettings | null }>('GET', '/integrations/email')).data });
  const [form, setForm] = useState({ host: '', port: 587, security: 'starttls' as EmailSecurity, user: '', password: '', fromName: '', fromAddress: '', replyTo: '' });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [preset, setPreset] = useState<string>('');

  useEffect(() => {
    const c = current.data;
    if (c) setPreset((p) => p || (PRESETS.find((x) => x.host === c.host)?.label ?? ''));
    if (c) setForm({ host: c.host, port: c.port, security: c.security, user: c.user, password: '', fromName: c.fromName ?? '', fromAddress: c.fromAddress, replyTo: c.replyTo ?? '' });
  }, [current.data]);

  const refresh = () => {
    client.invalidateQueries({ queryKey: ['integrations'] });
  };
  const save = useMutation({
    mutationFn: async () => (await api<{ data: EmailSettings }>('PUT', '/integrations/email', { ...form, port: Number(form.port), password: form.password || undefined, fromName: form.fromName || undefined, replyTo: form.replyTo || undefined })).data,
    onSuccess: () => {
      refresh();
      setForm((f) => ({ ...f, password: '' }));
      setMessage({ ok: true, text: 'Die Verbindung zum Mailserver steht, die Einrichtung ist gespeichert. Senden Sie jetzt eine Testmail an sich selbst.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const test = useMutation({
    mutationFn: async () => (await api<{ data: EmailTestResult }>('POST', '/integrations/email/test')).data,
    onSuccess: (r) => {
      refresh();
      setMessage({ ok: true, text: `Die Testmail ist an ${r.sentTo} unterwegs. Sieh in Ihrem Postfach nach, auch im Spam-Ordner.` });
    },
    onError: (e) => {
      refresh();
      setMessage({ ok: false, text: errorText(e, 'Die Testmail konnte nicht gesendet werden.') });
    },
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', '/integrations/email'),
    onSuccess: () => {
      refresh();
      setForm({ host: '', port: 587, security: 'starttls', user: '', password: '', fromName: '', fromAddress: '', replyTo: '' });
      setMessage({ ok: true, text: 'Die Verbindung ist getrennt, das gespeicherte Passwort gelöscht.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Trennen hat nicht geklappt.') }),
  });

  if (current.isLoading) return <p className="muted">Wird geladen …</p>;
  const saved = current.data;
  const passwordHelp = PRESETS.find((x) => x.label === preset)?.password;
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    save.mutate();
  };

  return (
    <>
      {saved?.status === 'error' && saved.lastError && <div className="alert alert--error" role="alert">Zuletzt hat der Versand nicht geklappt: {saved.lastError}</div>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <form className="card" onSubmit={submit} noValidate>
        <h2>Postfach</h2>
        <label className="field">
          <span>Anbieter (füllt Server, Port und Verschlüsselung aus)</span>
          <select
            className="input"
            value={preset}
            onChange={(e) => {
              const p = PRESETS.find((x) => x.label === e.target.value);
              setPreset(p?.label ?? '');
              setHint(p?.hint ?? null);
              if (p) setForm((f) => ({ ...f, host: p.host, port: p.port, security: p.security }));
            }}
          >
            <option value="">Bitte wählen oder unten selbst eintragen</option>
            {PRESETS.map((p) => <option key={p.label}>{p.label}</option>)}
          </select>
        </label>
        {passwordHelp ? (
          <div className="alert alert--warn" role="note">
            <strong>{passwordHelp.label} nötig.</strong> {passwordHelp.warning}{' '}
            <a href={passwordHelp.url} target="_blank" rel="noopener noreferrer">{passwordHelp.linkText}<span className="sr-only"> (öffnet in neuem Tab)</span></a>
          </div>
        ) : hint ? <p className="muted small">{hint}</p> : null}
        <div className="form-grid">
          <label className="field"><span>SMTP-Server <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={form.host} onChange={set('host')} placeholder="smtp.ionos.de" autoComplete="off" required /></label>
          <label className="field"><span>Port <abbr title="Pflichtfeld">*</abbr></span><input className="input" type="number" value={form.port} onChange={set('port')} min={1} max={65535} required /></label>
          <label className="field">
            <span>Verschlüsselung <abbr title="Pflichtfeld">*</abbr></span>
            <select className="input" value={form.security} onChange={set('security')}>
              <option value="starttls">{EMAIL_SECURITY_LABELS.starttls}</option>
              <option value="ssl">{EMAIL_SECURITY_LABELS.ssl}</option>
            </select>
          </label>
          <label className="field"><span>Benutzername <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={form.user} onChange={set('user')} placeholder="kontakt@ihre-firma.de" autoComplete="off" required /></label>
          <label className="field">
            <span>
              {passwordHelp?.label ?? 'Passwort'} {saved?.hasPassword ? '' : <abbr title="Pflichtfeld">*</abbr>}
              {passwordHelp && <InfoHint label={passwordHelp.label}>{passwordHelp.steps}</InfoHint>}
            </span>
            <input className="input" type="password" value={form.password} onChange={set('password')} placeholder={saved?.hasPassword ? 'unverändert, zum Ändern neues eintragen' : ''} autoComplete="new-password" />
          </label>
        </div>
        <h2>Absender</h2>
        <div className="form-grid">
          <label className="field"><span>Name des Absenders (freiwillig)</span><input className="input" value={form.fromName} onChange={set('fromName')} placeholder="Ihre Firma GmbH" maxLength={80} /></label>
          <label className="field"><span>Absenderadresse <abbr title="Pflichtfeld">*</abbr></span><input className="input" type="email" value={form.fromAddress} onChange={set('fromAddress')} placeholder="kontakt@ihre-firma.de" required /></label>
          <label className="field"><span>Antworten gehen an (freiwillig)</span><input className="input" type="email" value={form.replyTo} onChange={set('replyTo')} placeholder="wie Absenderadresse" /></label>
        </div>
        <p className="muted small">Die Absenderadresse muss zu Ihrem Postfach gehören. Die meisten Anbieter lehnen fremde Absender ab.</p>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird geprüft …' : 'Prüfen und speichern'}</button>
          {saved && <button className="btn btn--secondary" type="button" disabled={test.isPending} onClick={() => { setMessage(null); test.mutate(); }}>{test.isPending ? 'Wird gesendet …' : 'Testmail an mich senden'}</button>}
          {saved && <button className="btn btn--secondary" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm('Verbindung trennen? Wirkia kann danach keine E-Mails mehr in Ihrem Namen versenden.')) remove.mutate(); }}>Trennen</button>}
        </div>
      </form>
    </>
  );
}
